import { lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router';
const Tournaments = lazy(() => import('src/pages/Tournaments'));
const AccountDeposit = lazy(() => import('src/pages/AccountDeposit'));
// Commented by ticket FE-6908. Uncomment after updating the functionality
// const PersonalJackpots = lazy(() => import('src/pages/AccountJackpots'));
const AccountProfile = lazy(() => import('src/pages/AccountProfile'));
const PersonalAreaPromo = lazy(() => import('src/pages/AccountPromo'));
const AccountTournaments = lazy(() => import('src/pages/AccountTounaments'));
const AccountVip = lazy(() => import('src/pages/AccountVip/AccountVip'));
const PersonalAreaWallet = lazy(() => import('src/pages/AccountWallet'));
const AccountWithdrawal = lazy(() => import('src/pages/AccountWithdrawal'));
const Bonus = lazy(() => import('src/pages/Bonus'));
const Bonuses = lazy(() => import('src/pages/Bonuses'));
const ContentPage = lazy(() => import('src/pages/ContentPage'));
const Faqs = lazy(() => import('src/pages/Faqs'));
const GamePage = lazy(() => import('src/pages/Game'));
const Games = lazy(() => import('src/pages/Games'));
const Landing = lazy(() => import('src/pages/Landing'));
// Commented by ticket FE-6908. Uncomment after updating the functionality
const Jackpots = lazy(() => import('src/pages/Jackpots'));
const LocalTournament = lazy(() => import('src/pages/LocalTournament'));
const MainPage = lazy(() => import('src/pages/Main'));
const ProviderTournament = lazy(() => import('src/pages/ProviderTournament'));
const PwaPage = lazy(() => import('src/pages/PwaPage'));
const VIP = lazy(() => import('src/pages/VIP'));
const DepositStandalonePage = lazy(() => import('src/pages/DepositStandalone'));
const MockModalsPage = lazy(() => import('src/modals/mocks'));
const CardGame = lazy(() => import('src/modules/cardGame'));
const CatchGame = lazy(() => import('src/modules/catchGame'));
const EventShop = lazy(() => import('src/modules/EventShop'));
const SportPage = lazy(() => import('src/pages/Sport'));

import { useFullScreen } from 'src/hooks/useFullScreen';
import { FreeSpinsHandler } from './components/FreeSpinsHandler';
import { GameLayout } from '../layout/GameLayout';
import { AnimatePresence } from 'framer-motion';
import { useApiContext, useGetBlockedCountry } from 'react-easyrocket';
import { isDev, isProductionEnv } from 'src/api';
import { Page404 } from 'src/pages/Page404';
import { SocialSignUpHandler } from 'src/router/components/SocialSignUpHandler';
import { LANG_PARAM, useRoute } from 'src/router/hooks/useRoute';
import ReferralPage from 'src/pages/ReferralPage';
import { PageHeadSEO } from 'src/router/components/PageHeadSEO/PageHeadSEO';
import { ProtectedAuthRoute } from 'src/router/components/ProtectedAuthRoute';
import { AccountLayout } from 'src/layout/AccountLayout';
import { Layout } from 'src/layout/Layout';
import { LiveChat } from 'src/modules/livechat/components/LiveChat';
import { MessagesContainer } from 'src/modules/messages/components/MessagesContainer';
import { isShowEvent } from 'src/config/event/utils';
import { resetPreloadRetries } from 'src/utils/preloadRetry';
import { WELCOME_BONUS_ID, WELCOME_BONUS_VIP_ID } from 'src/pages/Landing/components/constant';
import { OpenRegistrationByUrl } from './components/OpenRegistrationByUrl';
import { useSaveOpenedModal } from './hooks/useSaveOpenedModal';
import { LuckyPigeonOpener } from './components/LuckyPigeonOpener';
import { DoSomethingOnEveryPage } from './components/DoSomethingOnEveryPage';
import { useCloseModalOnBackForward } from './hooks/useCloseModalOnBackForward';
import { GptGamesBanners } from './components/GptGamesBanners';
import { BlockedCountryPlug } from './components/BlockedCountryPlug';
import { EmailConfirmationHandler } from './components/EmailConfirmationHandler';
import Modal from 'src/modals';
import { ActivateBonus } from 'src/pages/ActivateBonus';
import { LanguageLayout } from 'src/layout/LanguageLayout';
import { AuthRoute } from 'src/router/components/AuthRoute';
import { DepositLink } from 'src/components/DepositLink';
import { DepositReturn } from './components/DepositReturn';

export function Router() {
  const { isAuthenticated } = useApiContext();
  const { refFullscreen } = useFullScreen();
  const location = useLocation();
  const { isBlockedCountry } = useGetBlockedCountry();
  const { ROUTE } = useRoute();
  useCloseModalOnBackForward();

  useEffect(() => {
    resetPreloadRetries();
  }, []);
  useSaveOpenedModal();

  if (isBlockedCountry) {
    return <BlockedCountryPlug />;
  }

  return (
    <div ref={refFullscreen} className="theme">
      <DoSomethingOnEveryPage />
      <PageHeadSEO />
      <MessagesContainer />
      <Modal />
      <AnimatePresence initial={false}>
        <Routes location={location} key="location">
          <Route path={LANG_PARAM} element={<LanguageLayout />}>
            <Route path={ROUTE.LOGIN.path} element={<AuthRoute mode="login" />} />
            <Route path={ROUTE.SIGNUP.path} element={<AuthRoute mode="signup" />} />
            <Route
              path={ROUTE.ACCOUNT.path}
              element={
                <ProtectedAuthRoute>
                  <AccountLayout />
                </ProtectedAuthRoute>
              }>
              <Route index element={<Navigate to={ROUTE.ACCOUNT_PROFILE_MAIN.path} />} />
              <Route path={ROUTE.ACCOUNT_PROFILE.path} element={<AccountProfile />} />
              <Route
                path={ROUTE.ACCOUNT_PROFILE_TOURNAMENTS.path}
                element={<AccountTournaments />}
              />
              {/* Commented by ticket FE-6908. Uncomment after updating the functionality
            <Route path={ROUTE.ACCOUNT_JACKPOTS.path} element={<PersonalJackpots />} />*/}
              <Route path={ROUTE.ACCOUNT_USER_BALANCE.path} element={<PersonalAreaWallet />} />
              <Route path={ROUTE.ACCOUNT_DEPOSIT.path} element={<AccountDeposit />} />
              <Route path={ROUTE.ACCOUNT_WITHDRAWAL.path} element={<AccountWithdrawal />} />
              <Route path={ROUTE.ACCOUNT_BONUS.path} element={<PersonalAreaPromo />} />
              <Route path={ROUTE.ACCOUNT_VIP.path} element={<AccountVip />} />
            </Route>

            <Route path={''} element={<Layout />}>
              <Route index element={<MainPage />} />
              <Route path={ROUTE.DEPOSIT_LINK.path} element={<DepositLink />} />
              <Route path={ROUTE.AUTH.path} element={<SocialSignUpHandler />} />
              <Route path={ROUTE.GAMES.path} element={<Games />} />
              <Route path={ROUTE.TOURNAMENTS.path} element={<Tournaments />} />
              <Route path={ROUTE.NETWORK_TOURNAMENT.path} element={<ProviderTournament />} />
              <Route path={ROUTE.TOURNAMENTS_LOCAL.path} element={<LocalTournament />} />
              <Route path={ROUTE.BONUSES.path} element={<Bonuses />} />
              <Route path={ROUTE.BONUS.path} element={<Bonus />} />
              <Route path={ROUTE.INFO_PAGES.path} element={<ContentPage />} />
              <Route path={ROUTE.JACKPOTS.path} element={<Jackpots />} />
              <Route path={ROUTE.FAQ.path} element={<Faqs />} />
              <Route path={ROUTE.VIP.path} element={<VIP />} />
              <Route path={ROUTE.PWA_INSTRUCTION.path} element={<PwaPage />} />
              <Route path={ROUTE.SPORT.path} element={<SportPage />} />
              <Route path={ROUTE[404].path} element={<Page404 />} />
              <Route
                path={ROUTE.EVENT_SHOP.path}
                element={isShowEvent('EVENT_SHOP') ? <EventShop /> : <Navigate to="/" />}
              />
              {(isDev || !isProductionEnv) && (
                <Route path="mock-modals" element={<MockModalsPage />} />
              )}
              <Route
                path={ROUTE.CATCH_GAME.path}
                element={isShowEvent('CATCH_GAME') ? <CatchGame /> : <Navigate to="/" />}
              />
              <Route
                path={ROUTE.CARD_GAME.path}
                element={isShowEvent('CARD_GAME') ? <CardGame /> : <Navigate to="/" />}
              />
            </Route>
            <Route path={ROUTE.GAME.path} element={<GameLayout />}>
              <Route index element={<GamePage />} />
            </Route>
            <Route path={ROUTE.DEPOSIT_STANDALONE.path} element={<DepositStandalonePage />} />
            <Route path={ROUTE.ACTIVATE_BONUS.path} element={<ActivateBonus />} />
            <Route path={ROUTE.DEPOSIT_SUCCESS.path} element={<DepositReturn status="success" />} />
            <Route path={ROUTE.DEPOSIT_FAILURE.path} element={<DepositReturn status="failure" />} />
            <Route path={ROUTE.DEPOSIT_CANCEL.path} element={<DepositReturn status="cancel" />} />
            <Route path={ROUTE.DEPOSIT_RETURN.path} element={<DepositReturn status="return" />} />
            <Route path={ROUTE.GPT_LUCKY_PIGEON.path} element={<LuckyPigeonOpener />} />
            <Route
              path={ROUTE.LANDING_WELCOME_BONUS.path}
              element={
                isAuthenticated ? (
                  <Navigate to={ROUTE.MAIN.path} />
                ) : (
                  <Landing bonusID={`${WELCOME_BONUS_ID}`} />
                )
              }
            />
            <Route
              path={ROUTE.LANDING_WELCOME_BONUS_VIP.path}
              element={
                isAuthenticated ? (
                  <Navigate to={ROUTE.MAIN.path} />
                ) : (
                  <Landing bonusID={`${WELCOME_BONUS_VIP_ID}`} />
                )
              }
            />
            <Route path={ROUTE.REGISTRATION.path} element={<OpenRegistrationByUrl />} />
            <Route path={ROUTE.REGISTER_REFERRAL.path} element={<ReferralPage />} />
            <Route path={ROUTE.ACTION.path} element={<EmailConfirmationHandler />} />
            <Route
              path={ROUTE.ALL_ROUTES.path}
              element={<Navigate to={ROUTE[404].get()} replace />}
            />
          </Route>
        </Routes>
        <GptGamesBanners key="banners" />
        <FreeSpinsHandler key="freespinshandler" />
        <LiveChat />
      </AnimatePresence>
    </div>
  );
}
